import type { CSSProperties } from "react";
import {
	OCTAGON_BUTTON_ACTIVE_GRADIENT_FROM,
	OCTAGON_BUTTON_ACTIVE_GRADIENT_TO,
	OCTAGON_BUTTON_INNER_FILL,
	OCTAGON_BUTTON_INNER_STROKE,
} from "./octagon-geometry";

/** CSS custom properties consumed by {@link octagon-badge-surfaces.css}. */
export const OCTAGON_BADGE_LAYER_CSS_VARS = {
	shellBg: "--sq-octagon-badge-shell-bg",
	shellInset: "--sq-octagon-badge-shell-inset",
	strokeBg: "--sq-octagon-badge-stroke-bg",
	strokeBgImage: "--sq-octagon-badge-stroke-bg-image",
	faceBg: "--sq-octagon-badge-face-bg",
	faceBgImage: "--sq-octagon-badge-face-bg-image",
} as const;

export type OctagonBadgeSurface = "button" | "skill" | "custom";

/** Skill badge chrome — `earned` (gold reward) or `placeholder` (locked / unearned grey). */
export type SkillBadgeVariant = "earned" | "placeholder";

/** Skill badge inner face gradient (top → bottom). Mirrored in `octagon-badge-surfaces.css`. */
export const SKILL_BADGE_FACE_GRADIENT_FROM = "#1c2230";
export const SKILL_BADGE_FACE_GRADIENT_TO = "#080a0f";

export interface OctagonBadgeLayerStyle {
	backgroundColor?: string;
	backgroundImage?: string;
	boxShadow?: string;
	opacity?: number;
}

export interface OctagonBadgeLayerOverrides {
	shell?: OctagonBadgeLayerStyle;
	stroke?: OctagonBadgeLayerStyle;
	face?: OctagonBadgeLayerStyle;
}

function layerStyleToCssVars(
	layer: OctagonBadgeLayerStyle | undefined,
	prefix: "shell" | "stroke" | "face",
): CSSProperties {
	if (!layer) {
		return {};
	}
	const vars: Record<string, string | number> = {};
	if (layer.backgroundColor != null) {
		vars[
			prefix === "shell"
				? OCTAGON_BADGE_LAYER_CSS_VARS.shellBg
				: prefix === "stroke"
					? OCTAGON_BADGE_LAYER_CSS_VARS.strokeBg
					: OCTAGON_BADGE_LAYER_CSS_VARS.faceBg
		] = layer.backgroundColor;
	}
	if (layer.backgroundImage != null) {
		vars[
			prefix === "stroke"
				? OCTAGON_BADGE_LAYER_CSS_VARS.strokeBgImage
				: OCTAGON_BADGE_LAYER_CSS_VARS.faceBgImage
		] = layer.backgroundImage;
	}
	if (layer.boxShadow != null && prefix === "shell") {
		vars[OCTAGON_BADGE_LAYER_CSS_VARS.shellInset] = layer.boxShadow;
	}
	if (layer.opacity != null) {
		vars[`--sq-octagon-badge-${prefix}-opacity`] = layer.opacity;
	}
	return vars as CSSProperties;
}

export function resolveOctagonBadgeLayerStyle(
	surface: OctagonBadgeSurface,
	overrides?: OctagonBadgeLayerOverrides,
): CSSProperties {
	const preset = OCTAGON_BADGE_SURFACE_PRESETS[surface];
	return {
		...preset,
		...layerStyleToCssVars(overrides?.shell, "shell"),
		...layerStyleToCssVars(overrides?.stroke, "stroke"),
		...layerStyleToCssVars(overrides?.face, "face"),
	};
}

const OCTAGON_BADGE_SURFACE_PRESETS: Record<
	OctagonBadgeSurface,
	CSSProperties
> = {
	button: {
		[OCTAGON_BADGE_LAYER_CSS_VARS.shellBg]: "rgb(255 255 255 / 0.48)",
		[OCTAGON_BADGE_LAYER_CSS_VARS.shellInset]:
			"inset 0 0 0 1px rgb(255 255 255 / 0.72)",
		[OCTAGON_BADGE_LAYER_CSS_VARS.strokeBg]: OCTAGON_BUTTON_INNER_STROKE,
		[OCTAGON_BADGE_LAYER_CSS_VARS.strokeBgImage]: "none",
		[OCTAGON_BADGE_LAYER_CSS_VARS.faceBg]: OCTAGON_BUTTON_INNER_FILL,
		[OCTAGON_BADGE_LAYER_CSS_VARS.faceBgImage]: "none",
	} as CSSProperties,
	skill: {
		[OCTAGON_BADGE_LAYER_CSS_VARS.shellBg]: "transparent",
		[OCTAGON_BADGE_LAYER_CSS_VARS.shellInset]: "none",
		[OCTAGON_BADGE_LAYER_CSS_VARS.strokeBg]: "transparent",
		[OCTAGON_BADGE_LAYER_CSS_VARS.strokeBgImage]:
			"var(--sq-skill-badge-rim-gradient)",
	} as CSSProperties,
	custom: {},
};

/** Active button face — matches battle octagon selected state. */
export const OCTAGON_BADGE_BUTTON_ACTIVE_FACE_STYLE = {
	[OCTAGON_BADGE_LAYER_CSS_VARS.faceBg]: "transparent",
	[OCTAGON_BADGE_LAYER_CSS_VARS.faceBgImage]: `linear-gradient(135deg, ${OCTAGON_BUTTON_ACTIVE_GRADIENT_FROM} 0%, ${OCTAGON_BUTTON_ACTIVE_GRADIENT_TO} 100%)`,
} as CSSProperties;
